'use client';

import { isSignOutInProgress } from '@/shared/auth/sign-out';
import { signalWorkerPushSessionEnding, startWorkerPushRegistrationSync } from '@/shared/auth/worker-push';
import { useIsSafeonFlutterClient } from '@/shared/forced-mobile/ForcedMobileProvider';
import { useSession } from 'next-auth/react';
import { useEffect, useRef } from 'react';

export function WorkerPushRegistrationSync() {
  const { data: session, status, update } = useSession();
  const isFlutterClient = useIsSafeonFlutterClient();
  const identity = session?.mobileAuth?.mobile_context;
  const identityKey = identity ? [identity.user_uid, identity.field_uid, identity.device_uid, identity.session_uid].join(':') : '';
  const refreshState = session?.refreshState;
  const accessToken = session?.accessToken;
  const updateRef = useRef(update);
  const authRefreshRef = useRef<{ identityKey: string; accessToken?: string } | null>(null);

  useEffect(() => {
    updateRef.current = update;
  }, [update]);

  useEffect(() => {
    if (!isFlutterClient || status === 'loading') return;
    if (status !== 'authenticated' || !identityKey || refreshState === 'terminal' || isSignOutInProgress()) {
      signalWorkerPushSessionEnding();
      return;
    }
    if (refreshState === 'retryable') return;
    return startWorkerPushRegistrationSync(
      () => {
        // NextAuth's loading transition restarts this effect; keep the same failed credential bounded across restarts.
        if (authRefreshRef.current?.identityKey === identityKey && authRefreshRef.current.accessToken === accessToken) return;
        authRefreshRef.current = { identityKey, accessToken };
        void updateRef.current();
      },
      () => {
        if (authRefreshRef.current?.identityKey === identityKey && authRefreshRef.current.accessToken === accessToken) authRefreshRef.current = null;
      }
    );
  }, [accessToken, identityKey, isFlutterClient, refreshState, status]);

  return null;
}
